import { Line, Text } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useRef } from "react"
import * as THREE from 'three'
import { useActsStore } from "../../../store/useActsStore"
import { movePropertiesRange } from "../../../utils/moveObjects"

export default function Lines() {
    const lines = useRef()
    const texts = useRef()

    const textsContent = useRef([
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Three.js React Three Fiber Zustand",
        "Storytelling Illustration Immersive website",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Three.js React Three Fiber Zustand",
        "Storytelling Illustration Immersive website",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Three.js React Three Fiber Zustand",
        "Storytelling Illustration Immersive website",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Figma - User experience / User interface",
        "Three.js React Three Fiber Zustand",
        "Three.js React Three Fiber Zustand",
        "Storytelling Illustration Immersive website",

    ])

    const points = [
        [5, -8, -10], [40, -8, -10],
        [5, -4, -10], [40, -4, -10],
        [5, 0, -10], [40, 0, -10],
        [5, 4, -10], [40, 4, -10],
        [5, 8, -10], [40, 8, -10],

        [5, -8, 0], [40, -8, 0],
        [5, -4, 0], [40, -4, 0],
        [5, 0, 0], [40, 0, 0],
        [5, 4, 0], [40, 4, 0],
        [5, 8, 0], [40, 8, 0],

        [5, -8, 10], [40, -8, 10],
        [5, -4, 10], [40, -4, 10],
        [5, 0, 10], [40, 0, 10],
        [5, 4, 10], [40, 4, 10],
        [5, 8, 10], [40, 8, 10],

        [10, -16, -8], [10, 16, -8],
        [15, -16, -8], [15, 16, -8],
        [20, -16, -8], [20, 16, -8],
        [25, -16, -8], [25, 16, -8],
        [30, -16, -8], [30, 16, -8],
        [35, -16, -8], [35, 16, -8],

        [10, -16, 2], [10, 16, 2],
        [15, -16, 2], [15, 16, 2],
        [20, -16, 2], [20, 16, 2],
        [25, -16, 2], [25, 16, 2],
        [30, -16, 2], [30, 16, 2],
        [35, -16, 2], [35, 16, 2],

        [12, -6, -18], [12, -6, 18],
        [18, -2, -18], [18, -2, 18],
        [24, 2, -18], [24, 2, 18],
        [30, 6, -18], [30, 6, 18],
        [36, 0, -18], [36, 0, 18],
    ]

    const basePoints = useRef(points)
    const temp = useRef([...points])

    // ------- TEXTS PRECOMPUTE (one time) -------
    const labels = useRef(
        Array.from({ length: points.length / 2 }).map((_, k) => {
            const i = k * 2
            const a = points[i]
            const b = points[i + 1]

            const aV = new THREE.Vector3(a[0], a[1], a[2])
            const bV = new THREE.Vector3(b[0], b[1], b[2])

            const mid = new THREE.Vector3().addVectors(aV, bV).multiplyScalar(0.5)

            const dir = new THREE.Vector3().subVectors(bV, aV).normalize()
            const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), dir)

            return {
                k,
                mid: mid.toArray(),
                dir: dir.toArray(),
                quat: quat.toArray(),
                jitter: 10,
            }
        })
    )

    // temp vecs (no alloc in loop)
    const vPos = useRef(new THREE.Vector3())
    const vDir = useRef(new THREE.Vector3())

    useFrame(() => {
        const { getCurrentActId } = useActsStore.getState()
        const id = getCurrentActId()
        if (id != 1) return

        const stateActe = useActsStore.getState()
        const holdProgress = stateActe.actsProgress[id]?.holdProgress ?? 0

        // ----- lines opacity (unchanged) -----
        movePropertiesRange(
            id,
            { current: lines.current.material },
            "opacity",
            0,
            0.6,
            holdProgress,
            0.3,
            0.6,
            false
        )

        if (holdProgress > 0.8) {
            movePropertiesRange(
                id,
                { current: lines.current.material },
                "opacity",
                1,
                -1,
                holdProgress,
                0.6,
                1
            )
        }

        // ----- t for segment reveal (unchanged) -----
        const start = 0.3
        const end = 0.6

        let t = 0
        if (holdProgress <= start) t = 0
        else if (holdProgress >= end) t = 1
        else t = (holdProgress - start) / (end - start)

        for (let i = 0; i < basePoints.current.length; i += 2) {
            const p0 = basePoints.current[i]
            const p1 = basePoints.current[i + 1]

            temp.current[i] = p0
            temp.current[i + 1] = [
                THREE.MathUtils.lerp(p0[0], p1[0], t),
                THREE.MathUtils.lerp(p0[1], p1[1], t),
                THREE.MathUtils.lerp(p0[2], p1[2], t),
            ]
        }
        lines.current.geometry.setPositions(temp.current.flat())

        // ----- texts opacity + translation along segment -----
        if (texts.current) {

            const slideT = t


            let alpha = 0
            if (holdProgress <= 0.3) alpha = 0
            else if (holdProgress < 0.6) alpha = (holdProgress - 0.3) / (0.6 - 0.3)
            else if (holdProgress < 0.8) alpha = 1
            else if (holdProgress <= 1) alpha = 1 - (holdProgress - 0.8) / (1 - 0.8)
            else alpha = 0

            const children = texts.current.children
            const translateAmount = 0.8
            const offset = 0.6

            const up0 = new THREE.Vector3(0, 1, 0)
            const up1 = new THREE.Vector3(1, 0, 0)
            const side = new THREE.Vector3()

            for (let idx = 0; idx < children.length; idx++) {
                const child = children[idx]
                const data = labels.current[idx]
                if (!data) continue

                vPos.current.fromArray(data.mid)
                vDir.current.fromArray(data.dir)

                // slide stable (ne dépend plus de alpha)
                vPos.current.addScaledVector(vDir.current, slideT * translateAmount)

                // offset perpendiculaire
                const up = Math.abs(vDir.current.y) > 0.9 ? up1 : up0
                side.crossVectors(vDir.current, up).normalize()
                vPos.current.addScaledVector(side, offset + data.jitter)
                child.position.copy(vPos.current)

                if (child.material) {
                    child.material.transparent = true
                    child.material.opacity = alpha
                }

                child.visible = alpha > 0.001
            }
        }
    })

    const dark = new THREE.Color("#393939").toArray()
    const light = new THREE.Color("#1a1a1a").toArray()

    const vertexColors = points.map((_, i) => {
        const isStart = i % 2 === 0
        return isStart ? dark : light
    })

    return (
        <>
            <group rotation={[-0.8, 0, 0]} position={[-2.5, 0, 0]}>
                <Line
                    ref={lines}
                    points={points}
                    segments
                    lineWidth={1}
                    vertexColors={vertexColors}
                    transparent
                    depthWrite={false}
                    opacity={0.0}
                    dashed
                    dashSize={10}
                    gapSize={2}
                    dashScale={1}
                />

                {/* TEXT GROUP (animated via ref) */}
                <group ref={texts} visibleOpacity={0}>
                    {labels.current.map((d) => (
                        <Text
                            key={d.k}
                            position={d.mid}
                            quaternion={new THREE.Quaternion().fromArray(d.quat)}
                            fontSize={0.15}
                            anchorX="center"
                            anchorY="middle"
                            color={"#1a1a1a"}
                            font={"font/Mattone-Regular.woff"}
                        >
                            {textsContent.current[d.k] ?? ""}
                        </Text>
                    ))}
                </group>
            </group>
        </>
    )
}